import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const GoogleSignInButton = ({ text = "signin_with" }) => {
  const { t, i18n } = useTranslation();
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  // Google renders its OWN button label ("Sign up with Google" / "S'inscrire
  // avec Google" / …) from the `locale` we pass — so it MUST track the app
  // language, otherwise the button stays French on a Korean/Arabic/… page.
  const lang = i18n.language || "en";

  useEffect(() => {
    if (!window.google || !GOOGLE_CLIENT_ID) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        try {
          await googleLogin(response.credential);
          toast.success(t("auth.google.success", "Connexion Google réussie !"));
          navigate("/dashboard");
        } catch (error) {
          console.error("Google login error:", error);
          toast.error(t("auth.google.error", "Erreur de connexion Google"));
        }
      },
    });

    // GSI renderButton width must be an INTEGER px within Google's [200, 400]
    // range; "100%" is rejected (console: GSI_LOGGER "Provided button width is
    // invalid: 100%") and the button silently falls back to a default width.
    // Measure the container and clamp to the allowed range (360 fallback if the
    // container hasn't laid out yet).
    const px = Math.round(buttonRef.current?.offsetWidth || 0);
    const width = Math.min(400, Math.max(200, px || 360));

    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "filled_black",
      size: "large",
      width,
      text: text,
      shape: "pill",
      locale: lang,
    });
  }, [googleLogin, navigate, text, t, lang]);

  return <div ref={buttonRef} className="w-full flex justify-center" />;
};

export default GoogleSignInButton;
